import * as THREE from "three";
/**
 *
 * Creates a pinned video in the scene
 */
export default class PinnedVideo extends THREE.Mesh {
  constructor(v) {
    const { coordinates, uuid, url } = v;

    // Create geom
    const geometry = new THREE.Geometry();

    // Create material
    const material = new THREE.MeshBasicMaterial({ color: 0xffffff });
    material.side = THREE.DoubleSide;

    const worldPos = [];
    var count = 0;
    for (const coord of coordinates) {
      const pos = normToWorld(new THREE.Vector2(coord.u, coord.v));

      if (count === 3) {
        var newPt = getCoplanarPoint(
          worldPos[0],
          worldPos[1],
          worldPos[2],
          pos,
        );
        worldPos.push(new THREE.Vector3(newPt.x, newPt.y, newPt.z));
      } else {
        worldPos.push(new THREE.Vector3(pos.x, pos.y, pos.z));
      }

      count++;
    }

    if (worldPos.length !== 4) {
      ErrorMessage();
    }

    const n = 20.0; //counts of slices in each direction
    const v01 = new THREE.Vector3(
      (worldPos[1].x - worldPos[0].x) / n,
      (worldPos[1].y - worldPos[0].y) / n,
      (worldPos[1].z - worldPos[0].z) / n,
    );
    const v12 = new THREE.Vector3(
      (worldPos[2].x - worldPos[1].x) / n,
      (worldPos[2].y - worldPos[1].y) / n,
      (worldPos[2].z - worldPos[1].z) / n,
    );
    const v03 = new THREE.Vector3(
      (worldPos[3].x - worldPos[0].x) / n,
      (worldPos[3].y - worldPos[0].y) / n,
      (worldPos[3].z - worldPos[0].z) / n,
    );
    const v32 = new THREE.Vector3(
      (worldPos[2].x - worldPos[3].x) / n,
      (worldPos[2].y - worldPos[3].y) / n,
      (worldPos[2].z - worldPos[3].z) / n,
    );

    var index = 0;
    for (var i = 0; i < n; i++) {
      for (var j = 0; j < n; j++) {
        const basePt0 = worldPos[0].clone().add(v01.clone().multiplyScalar(i));
        const basePt1 = worldPos[1].clone().add(v12.clone().multiplyScalar(j));
        const basePt2 = worldPos[3].clone().add(v32.clone().multiplyScalar(i));
        const basePt3 = worldPos[0].clone().add(v03.clone().multiplyScalar(j));
        const basePt4 = worldPos[0]
          .clone()
          .add(v01.clone().multiplyScalar(i + 1));
        const basePt5 = worldPos[1]
          .clone()
          .add(v12.clone().multiplyScalar(j + 1));
        const basePt6 = worldPos[3]
          .clone()
          .add(v32.clone().multiplyScalar(i + 1));
        const basePt7 = worldPos[0]
          .clone()
          .add(v03.clone().multiplyScalar(j + 1));

        var pt0 = new THREE.Vector3();
        var pt1 = new THREE.Vector3();
        var pt2 = new THREE.Vector3();
        var pt3 = new THREE.Vector3();

        if (!LineLineIntersect(basePt0, basePt2, basePt1, basePt3, pt0)) {
          ErrorMessage();
        }
        if (!LineLineIntersect(basePt4, basePt6, basePt1, basePt3, pt1)) {
          ErrorMessage();
        }
        if (!LineLineIntersect(basePt4, basePt6, basePt5, basePt7, pt2)) {
          ErrorMessage();
        }
        if (!LineLineIntersect(basePt0, basePt2, basePt5, basePt7, pt3)) {
          ErrorMessage();
        }

        geometry.vertices.push(new THREE.Vector3(pt0.x, pt0.y, pt0.z));
        index++;
        geometry.vertices.push(new THREE.Vector3(pt1.x, pt1.y, pt1.z));
        index++;
        geometry.vertices.push(new THREE.Vector3(pt2.x, pt2.y, pt2.z));
        index++;
        geometry.vertices.push(new THREE.Vector3(pt3.x, pt3.y, pt3.z));
        index++;

        geometry.faces.push(
          new THREE.Face3(index - 4, index - 3, index - 2),
          new THREE.Face3(index - 4, index - 2, index - 1),
        );
      }
    }

    geometry.computeBoundingSphere();

    const video = document.createElement("video");
    video.crossOrigin = "anonymous";

    video.src = url;
    video.loop = true;
    video.load();
    video.play();

    const texture = new THREE.VideoTexture(video);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.format = THREE.RGBFormat;

    material.map = texture;

    geometry.faceVertexUvs = [[]];

    for (let k = 0; k < n; k++) {
      for (let l = 0; l < n; l++) {
        geometry.faceVertexUvs[0].push([
          new THREE.Vector2((1 / n) * k, 1 - (1 / n) * l),
          new THREE.Vector2((1 / n) * (k + 1), 1 - (1 / n) * l),
          new THREE.Vector2((1 / n) * (k + 1), 1 - (1 / n) * (l + 1)),
        ]);
        geometry.faceVertexUvs[0].push([
          new THREE.Vector2((1 / n) * k, 1 - (1 / n) * l),
          new THREE.Vector2((1 / n) * (k + 1), 1 - (1 / n) * (l + 1)),
          new THREE.Vector2((1 / n) * k, 1 - (1 / n) * (l + 1)),
        ]);
      }
    }

    // Run super
    super(geometry, material);

    this._video = video;
    this._uuid = uuid;
  }

  toggle() {
    if (this._video.paused) {
      this._video.play();
    } else {
      this._video.pause();
    }
  }

  destroy() {
    this._video.pause();
    this.geometry.dispose();
    this.material.dispose();
  }

  getUUID() {
    return this._uuid;
  }
}
/**
 * Converts the normalized position to world coordinates.
 *
 * @param {THREE.Vector2} normPos
 * @return {THREE.Vector3} world coordinates
 */
const normToWorld = normPos => {
  var a = new THREE.Vector3(0, -500, 0);

  var b = new THREE.Euler(0, 2 * normPos.x * Math.PI, normPos.y * Math.PI);

  a.applyEuler(b);

  return a;
};

const getCoplanarPoint = (a, b, c, x) => {
  var p = new THREE.Vector3(0, 0, 0);

  const vec1 = b.clone().sub(a);
  const vec2 = c.clone().sub(a);
  var normal = new THREE.Vector3();
  normal.crossVectors(vec1, vec2);
  normal.normalize();

  var dir = x.clone();
  dir.normalize();

  if (normal.dot(dir) === 0) {
    ErrorMessage();
  }

  const t = normal.dot(a) / normal.dot(dir);

  p = dir.clone().multiplyScalar(t);
  return p;
};

/*
 Calculate the line segment PaPb that is the shortest route between
 two lines P1P2 and P3P4. Calculate also the values of mua and mub where
 Pa = P1 + mua (P2 - P1)
 Pb = P3 + mub (P4 - P3)
 Return FALSE if no solution exists.
 */
const LineLineIntersect = (p1, p2, p3, p4, pa) => {
  var p13 = new THREE.Vector3();
  var p43 = new THREE.Vector3();
  var p21 = new THREE.Vector3();

  var d1343, d4321, d1321, d4343, d2121;
  var numer, denom;
  var mua, mub;

  var pb = new THREE.Vector3();

  var EPS = 1e-5;

  p13.x = p1.x - p3.x;
  p13.y = p1.y - p3.y;
  p13.z = p1.z - p3.z;
  p43.x = p4.x - p3.x;
  p43.y = p4.y - p3.y;
  p43.z = p4.z - p3.z;
  if (Math.abs(p43.x) < EPS && Math.abs(p43.y) < EPS && Math.abs(p43.z) < EPS) {
    ErrorMessage();
    return false;
  }
  p21.x = p2.x - p1.x;
  p21.y = p2.y - p1.y;
  p21.z = p2.z - p1.z;
  if (Math.abs(p21.x) < EPS && Math.abs(p21.y) < EPS && Math.abs(p21.z) < EPS) {
    ErrorMessage();
    return false;
  }

  d1343 = p13.x * p43.x + p13.y * p43.y + p13.z * p43.z;
  d4321 = p43.x * p21.x + p43.y * p21.y + p43.z * p21.z;
  d1321 = p13.x * p21.x + p13.y * p21.y + p13.z * p21.z;
  d4343 = p43.x * p43.x + p43.y * p43.y + p43.z * p43.z;
  d2121 = p21.x * p21.x + p21.y * p21.y + p21.z * p21.z;

  denom = d2121 * d4343 - d4321 * d4321;
  if (Math.abs(denom) < EPS) {
    ErrorMessage();
    return false;
  }
  numer = d1343 * d4321 - d1321 * d4343;

  mua = numer / denom;
  mub = (d1343 + d4321 * mua) / d4343;

  pa.x = p1.x + mua * p21.x;
  pa.y = p1.y + mua * p21.y;
  pa.z = p1.z + mua * p21.z;
  pb.x = p3.x + mub * p43.x;
  pb.y = p3.y + mub * p43.y;
  pb.z = p3.z + mub * p43.z;

  if (
    Math.abs(pa.x - pb.x) < EPS &&
    Math.abs(pa.y - pb.y) < EPS &&
    Math.abs(pa.z - pb.z) < EPS
  ) {
    return true;
  } else {
    ErrorMessage();
    return false;
  }
};

const ErrorMessage = () => {
  window.alert("You didn't set the points incorrectly, please try again.");
};
